import {
  HeaderActionProps,
  Chat,
  FeedbackProps,
  SystemTurn,
  UserTurn,
  MultiStepForm,
  SingleStepForm,
} from "@tomis-tech/chat-ui";
import styled from "styled-components";

import { ChatWidgetConfiguration, FormInstance } from "@/types";
import { Toast } from "@/components/Toast";
import { useToast } from "@/hooks";

const ChatContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

type ChatOpenProps = Pick<
  ChatWidgetConfiguration,
  | "name"
  | "image"
  | "description"
  | "watermark"
  | "poweredByName"
  | "poweredByUrl"
> & {
  embed: boolean;
  feedback: FeedbackProps["mode"];
  handleSend: (text: string) => Promise<boolean>;
  /** Called as the customer types, to publish live-chat typing presence. */
  onType?: () => void;
  restartSession: () => Promise<any>;
  closeWidget?: () => void;
  /** All messages sent by chatbot and customer */
  turns: Array<SystemTurn | UserTurn>;
  isTyping?: boolean;
  /** Conversation-level forms and overlay state */
  forms?: FormInstance[];
  activeFormKey?: string | null;
  closeForm?: () => void;
  updateFormValues?: (patch: Record<string, any>) => void;
  submitForm?: () => Promise<boolean>;
  /** Optional callback when the "Powered by TOMIS" link is clicked */
  onPoweredByClick?: () => void;
  /** Status update shown at the top of the open chat. */
  banner?: React.ReactNode;
};

/**
 * The entire **OPEN** chat widget.
 *
 * Displays the chat interface with header, messages, and input.
 * Supports form overlays for forms triggered within the chat context.
 */
export const ChatOpen: React.FC<ChatOpenProps> = ({
  description,
  image,
  name,
  watermark,
  poweredByName,
  poweredByUrl,
  embed,
  closeWidget,
  restartSession,
  handleSend,
  onType,
  turns,
  isTyping = false,
  forms = [],
  activeFormKey = null,
  closeForm = () => {},
  updateFormValues = () => {},
  submitForm = async () => false,
  onPoweredByClick,
  banner,
}) => {
  const isLoading = isTyping;
  const {
    message: toastMessage,
    visible: toastVisible,
    variant: toastVariant,
    hideToast,
  } = useToast();

  const headerActions: HeaderActionProps[] = [
    {
      icon: "refresh",
      size: 16,
      onClick: restartSession,
    },
  ];

  if (!embed) {
    headerActions.push({
      icon: "minus",
      size: 16,
      onClick: closeWidget,
    });
  }

  const activeForm = forms.find((f) => f.key === activeFormKey) || null;
  const activeFormValues = activeForm?.values ?? {};
  const activeFormSteps = activeForm?.schema.steps ?? [];
  const activeFormFields = activeForm?.schema.fields ?? [];

  return (
    <ChatContainer>
      <Chat
        description={description}
        handleSend={handleSend}
        onType={onType}
        hasEnded={false}
        headerActions={headerActions}
        image={image}
        name={name}
        restartSession={restartSession}
        turns={turns}
        watermark={watermark}
        onPoweredByClick={onPoweredByClick}
        poweredByName={poweredByName}
        poweredByUrl={poweredByUrl}
        indicator={isTyping}
        banner={banner}
        extra={
          activeForm ? (
            activeForm.schema.mode === "multi-step" ? (
              <MultiStepForm
                steps={activeFormSteps}
                values={activeFormValues}
                onChange={updateFormValues}
                onClose={closeForm}
                onSubmit={submitForm}
                busy={isLoading}
                dismissible={true}
              />
            ) : (
              <SingleStepForm
                title={activeForm.schema.title}
                subtitle={activeForm.schema.subtitle}
                instructions={activeForm.schema.instructions}
                onClose={closeForm}
                onSubmit={submitForm}
                busy={isLoading}
                fields={activeFormFields}
                onChange={updateFormValues}
                values={activeFormValues}
                buttonLabel={activeForm.schema.submitLabel}
                dismissible={true}
              />
            )
          ) : null
        }
      />
      <Toast
        message={toastMessage}
        visible={toastVisible}
        onClose={hideToast}
        variant={toastVariant}
      />
    </ChatContainer>
  );
};
